// ════════════════════════════════════════════════════════════════════
// TERROR — THE HALF-TURN HAND-OFF
// ────────────────────────────────────────────────────────────────────
// ONE function that owns everything that must happen when a half-turn
// changes hands, no matter who is driving the duel.
//
// WHY THIS FILE EXISTS. Terror has exactly three places a half-turn can
// end, and until this file existed each of them was a hand-written copy
// of the same sequence:
//
//   • Shatterfield.js · handleEndTurn   — the END TURN button
//   • Shatterfield.js · doEndTurn       — the per-turn timer expiring
//   • NPC.js          · npcHandOffTurn  — the ONLY way the NPC seat's
//                                         turn changes hands, in BOTH
//                                         the shipping game and the
//                                         headless balancer
//
// Three copies meant three different games. Two of them were provably
// wrong:
//
//   1. THE LEDGER ONLY EVER ROTATED IN ONE OF THEM. `animaGainedThisTurn`
//      / `animaLostThisTurn` are PER-HALF-TURN counters, and
//      buildEndOfHalfTurnUpdate — the thing that rotates them — had a
//      single call site: handleEndTurn. So the NPC seat's "this turn"
//      counters accumulated for the WHOLE DUEL, on every screen, in
//      every driver; and a human seat whose turn ended on the TIMER
//      never rotated either. Anything reading those counters read a
//      running total and called it a turn. (DG-0 I'nfini's "draw 1 if
//      you gained Anima this turn" was, for such a seat, permanently
//      true after its first Anima gain — a conditional draw that had
//      quietly become unconditional.)
//
//   2. THE PROPHECY CHECK WAS NOT IN ANY OF THEM. `checkProphecies` is
//      a WIN CONDITION and its only call site in the project was
//      Shatterfield's onValue listener — i.e. inside the renderer. A
//      duel with no renderer attached (headless driver, the balancer, a
//      replay, a seat that navigated away) had no Prophecies in it at
//      all. The balancer had a local patch papering over the hole,
//      which is a harness lying to itself about the engine.
//
// ⚠ R33 WINS THE RACE, AND THAT IS A RULING, NOT AN ACCIDENT.
// Asking for Prophecies at the BOUNDARY rather than on every Firebase
// snapshot means a board that BECOMES Prophecy-complete in the middle
// of a turn now wins at the hand-off instead of instantly. About eleven
// sites end the match mid-turn the moment a seat's Anima reaches 0
// (R33). If both land in the same half-turn, R33 gets there first and
// the Prophecy never fires. That is the intended reading — a Solrend
// already won on Anima is over — and it is written here so nobody
// "fixes" it later by moving the check back into a listener.
// The PLAYER-FACING behaviour is unchanged: the manual ProphecyButton
// (Shatterfield · handleActivateProphecy) still gives a human the
// instant win the moment the condition and the fare are both there.
//
// ⚠ THE RAW gameManager TAPS, ON PURPOSE. Shatterfield.js declares a
// LOCAL `updatePlayer` that shadows gameManager's — it redirects an
// `anima` key to `teamAnima/{teamId}` for shared-Anima 2v2 seats and
// runs sealPayload over the rest. This file uses gameManager's own
// functions and must NEVER carry an `anima` key in any write it makes.
// That is safe because every field it writes to a seat is per-seat
// bookkeeping that is never pooled across a team even when the life
// total is: the Anima LEDGER counters, and the seat's Khao — `khao`,
// `maxKhao` and `khaoSpentThisCycle` (R92b/R132; teammates share ONE
// Anima pool and NOTHING else, R127). ⚠ THIS LIST USED TO SAY "the
// Anima LEDGER counters" AND ONLY THAT, and it had been false since the
// refill moved here — a comment naming what a function writes has to be
// re-read every time the function writes something new. (All of them
// ride as slash paths inside the match-root write rather than as their
// own updatePlayer call — see THE COST OF A HAND-OFF below. Same paths,
// same values, one fewer round trip, and still no `anima` key.)
//
// ⚠ THIS FILE IS IN THE HEADLESS ENGINE CLOSURE (tools/harness/build.mjs
// mirrors it, reached through NPC.js). Keep it JSX-free and React-free
// or the harness build refuses, loudly, by name.
// ════════════════════════════════════════════════════════════════════

import {
  updateMatch, getMatch, handOffChair, denseNull,
  endMatch as endMatchRaw,
} from "./gameManager";
import {
  callOnClock, STARTING_KHAO,
  // R132 — the Max Khao climb is priced here now. MAX_KHAO_NAT is R13's
  // natural ceiling; khaoClimbFloor is R91's number raised by R127's lap
  // length, declared once in the catalogue. See THE CLIMB below.
  MAX_KHAO_NAT, khaoClimbFloor, MAX_KHAO_ABS, durationStillRunning,
} from "./rules";
import { getNextActiveSeat, isEliminated, seatCountOf, livingEnemySeatsOf, sharesAnimaWithTeam, teamOf, getEffectiveAnima } from "./matchSeats";
import { buildEndOfHalfTurnUpdate, checkProphecies, coWinningSeats } from "./prophecyEngine";
import { sealAnimaGain } from "./ruleGate";
import { ESCENDANTS, TOMES } from "./cards";
import { baseId } from "./necroSeal";
import { setHandOffHalfTurn } from "./matchOutcome";

// ════════════════════════════════════════════════════════════════════
// ⏳ endOnClock — THE ONE WAY A CLOCK CLOSES A SOLREND
// ════════════════════════════════════════════════════════════════════
// TERROR HAS TWO CLOCKS AND THEY MUST END A DUEL THE SAME WAY.
//   R93 · the second hand — 150 half-turns, asked by handOffHalfTurn
//         below, and the only one a headless duel can ever reach.
//   R87 · the twenty-minute wall clock — asked by a React effect in
//         Shatterfield.js, and the only one a slow human duel reaches.
// Both produce the SAME verdict object, out of the same pure
// `timeoutWinner`. Only one of them used to ask R98.
//
// ⚠ THE DIVERGENCE THIS FUNCTION EXISTS TO CLOSE. The R93 path argued
// at length that "a duel called on the clock is still a duel ending"
// and added the co-winner resolution to itself. R87 — the other hand of
// the same law — called `endMatchRaw(code, verdict.winner,
// verdict.runnerUp)` with no `winners` and no co-winner resolution at
// all. So whether a seat sitting on a fulfilled Prophecy won when the
// bell rang depended on WHICH BELL, which is not a rule anybody wrote.
//
// caP'n: *"prophecies do not prevent wins merely grant them, the other
// player can still win naturally or by their own prophecy"* — the
// ruling says nothing about which clock.
//
// `board` is the board the verdict was computed FROM, so the co-winner
// question is asked of the same snapshot that decided the winner.
// Returns the full winners list, primary first, so a caller can narrate
// a co-win instead of telling a co-winner they lost.
export async function endOnClock(code, board, verdict) {
  const winners = [verdict.winner];
  try {
    const co = coWinningSeats(board, verdict.winner) || [];
    for (const c of co) if (!winners.includes(c.id)) winners.push(c.id);
    if (co.length) {
      // R99 — a Prophecy is spent when it resolves, and co-winning IS a
      // resolution. One write with the co-win record so a reader can
      // never see half of it.
      const stamp = {};
      co.forEach((c) => { stamp[`players/${c.seat}/prophecySpent`] = c.prophecyId; });
      stamp.coWonByProphecy = co.map((c) => `${c.seat}:${c.prophecyId}`).join(",");
      await updateMatch(code, stamp);
    }
  } catch (e) { console.error("[R98] clock-path co-winner check failed:", e && e.message); }
  // endMatchRaw, not the escalation-aware wrapper. A duel called on the
  // clock must CLOSE, not hand a boss a Second Wind.
  await endMatchRaw(code, verdict.winner, verdict.runnerUp, winners);
  return winners;
}

// ── handOffHalfTurn(code, match, fromIdx, opts) ─────────────────────
//
// `match` is the snapshot the CALLER chose, passed by argument and
// never re-derived here, because the clock verdict must be asked about
// the board the caller is looking at. Two rules for callers:
//
//   • A REACT CALLER MUST PASS `matchRef.current`, NEVER the `match`
//     state variable. React state lags Firebase by a render, and a
//     hand-off computed from last render's board is a hand-off computed
//     from a board that no longer exists.
//   • NEVER pass a `withEffectiveAnima`-wrapped seat or match into
//     here. That wrapper overwrites a seat's own `anima` with the TEAM
//     pool, and this function's writes go to the raw per-seat path.
//
// opts:
//   nextIdx    — who plays next. Optional. Defaults to
//                getNextActiveSeat(match, fromIdx). A caller's value is
//                honoured EXCEPT when it names a seat that `match` says
//                is not living and there is another living seat to hand
//                the chair to — see WHO SITS DOWN NEXT below for why
//                that exception is exactly that narrow.
//   clockOpts  — forwarded to callOnClock ({ turnLimit, elapsedSeconds }).
//   skipProphecies — escape hatch. matchOutcome's elimination reseat
//                passes it, and must: that reseat can be reached from
//                INSIDE a Prophecy's own resolution.
//
// Returns:
//   { calledOnClock, verdict, prophecyEnded, prophecy, ended,
//     nextTurn, nextActive }
// so each caller can do its own UI and its own telemetry without
// re-reading the match to find out what just happened.
// ════════════════════════════════════════════════════════════════════
// ⭐ PHASE 5 · THE END PHASE, ONCE (2026-09-15, the unification pass)
// ────────────────────────────────────────────────────────────────────
// RULEZ_V2 ch.4: "End-of-turn effects resolve. Every being you control
// refreshes its Lyf to full — Fallen ones included. Your half-turn ends
// and the next seat's begins." And ch.8: Fallen "clears the moment its
// owner's own turn begins."
//
// Until tonight this phase existed THREE times: the board's END TURN
// button, the board's timer forfeit, and the brain's resetEndOfTurn —
// each with its own Lyf reset and latch list, and only the board paying
// the two "at the end of the turn" cards. The one-resolver differential
// found the drift on its first run: Khao Garden prints "+1 Max Khao AT
// THE END OF THE TURN", the board paid it there, and the brain's old
// executor paid it AT ACTIVATION. Two timings for one card. So Phase 5
// lives here, in the seam every driver already passes through, and the
// three copies are gone.
//
// What it does, for the OUTGOING seat, inside the chair transaction:
//   · R28   every being refreshes to its printed Lyf (+3 while a Cuirass
//           loan runs), and the per-Turn latches clear: attackedThisTurn,
//           attacksThisTurn, the Primera stat shifts (R83), the extra-swing
//           grants, animaRedirectAll, beastArmed.
//   · Khao Garden (+1) / Khao Arcadia (+2) to Max Khao, capped at the card
//           ceiling, for a copy that was ACTIVATED this half-turn (R105:
//           the payout belongs to a firing that was paid for). Stamped
//           `endPaidTurn` so the payout can never land twice.
//   · Restraining Ordeal (caP'n 2026-09-14): a living adversary holding
//           it face-up offers the outgoing seat 1 Anima if that seat took
//           no Lyt Attack this half-turn. Read BEFORE the latches clear;
//           written to the pooled total for a 2v2 seat.
// And for the INCOMING seat: every Fallen being rises (R26).
// ════════════════════════════════════════════════════════════════════
const cardOf = (id) => (id ? ESCENDANTS.find((c) => c.id === baseId(id)) || null : null);
const tomeOf = (id) => (id ? TOMES.find((c) => c.id === baseId(id)) || null : null);
export const END_TURN_MAX_KHAO_CARDS = { tome_khao_garden: 1, tome_khao_arcadia: 2 };

export function endPhaseEsclings(esclings) {
  return (Array.isArray(esclings) ? esclings : Object.values(esclings || {})).map((s) => {
    if (!s || typeof s !== "object") return s;
    const card = cardOf(s.escendant);
    let resetLyf = card ? (card.lyf ?? null) : (s.currentLyf ?? null);
    if (card && s.cuirassBuffFrom && resetLyf !== null) resetLyf += 3;
    return {
      ...s,
      attackedThisTurn: false, attacksThisTurn: 0,
      currentLyf: resetLyf,
      primeraLytBuff: 0, primeraLyfBuff: 0,
      animaRedirectAll: false, extraAttack: false, extraAttackAnimaOnly: false,
      beastArmed: false,
    };
  });
}

export function endPhasePaths(live, fromIdx, nextActive) {
  const paths = {};
  const seat = live?.players?.[fromIdx];
  if (!seat) return paths;
  const turn = live?.turn || 0;
  const rifts = Array.isArray(seat.field?.esclings) ? seat.field.esclings : Object.values(seat.field?.esclings || {});
  const swung = rifts.some((s) => s?.attackedThisTurn);

  // Restraining Ordeal — the offering, read before the latches clear.
  if (!swung) {
    const offered = livingEnemySeatsOf(live, fromIdx).some((a) =>
      Object.values(live.players?.[a]?.field?.tomes || {}).some((t) => t && typeof t === "object" && t.id === "tome_restraining_ordeal" && !t.faceDown));
    if (offered) {
      const cur = getEffectiveAnima(live, fromIdx);
      const next = sealAnimaGain(cur, 1, "turnEngine.endPhase · Restraining Ordeal");
      if (next > cur) {
        if (sharesAnimaWithTeam(live, fromIdx)) paths[`teamAnima/${teamOf(live, fromIdx)}`] = next;
        else paths[`players/${fromIdx}/anima`] = next;
        // The gain belongs to the half-turn that is ENDING, and the ledger
        // rotation (buildEndOfHalfTurnUpdate) has already moved this
        // half-turn's gains into animaGainedLastTurn — so it lands there.
        paths[`players/${fromIdx}/animaGainedLastTurn`] = (seat.animaGainedThisTurn || 0) + (next - cur);
      }
    }
  }

  // Chief Calidum's Cuirass — "Lend 3 Lyf for 3 turns": the loan comes home
  // when its clock runs out (R135: anchored where it was stamped, measured
  // through the same converter as every duration), and a borrower whose
  // source Cuirass is gone loses the +3. Until tonight only the board's END
  // TURN button knew this; the timer path and the brain never returned a
  // loan, so a Cuirass the brain lent lent forever.
  const myTomesArr = Array.isArray(seat.field?.tomes) ? [...seat.field.tomes] : Object.assign([null, null, null], seat.field?.tomes || {});
  const myRifts = endPhaseEsclings(seat.field?.esclings || []);
  const foreign = {};   // seatIdx -> { esclings: [...], tomes: [...] } for borrowers on other seats
  const fieldOf = (idx) => {
    if (idx === fromIdx) return { esclings: myRifts, tomes: myTomesArr };
    if (!foreign[idx]) {
      const f = live.players?.[idx]?.field || {};
      foreign[idx] = {
        esclings: Array.isArray(f.esclings) ? f.esclings.map((s) => (s && typeof s === "object" ? { ...s } : s)) : Object.values(f.esclings || {}).map((s) => ({ ...s })),
        tomes: Array.isArray(f.tomes) ? f.tomes.map((x) => (x && typeof x === "object" ? { ...x } : x)) : Object.assign([null, null, null], f.tomes || {}),
      };
    }
    return foreign[idx];
  };
  myTomesArr.forEach((tm, i) => {
    if (!tm || typeof tm !== "object" || baseId(tm.id) !== "tome_chief_calidum") return;
    if (!tm.cuirassLentTo || tm.cuirassLentUntil === undefined || tm.cuirassLentUntil === null) return;
    if (durationStillRunning(turn, tm.cuirassLentUntil)) return;
    const lent = tm.cuirassLentTo;
    myTomesArr[i] = { ...tm, currentLyf: tomeOf(tm.id)?.lyf ?? 3, cuirassLentTo: null, cuirassLentUntil: null };
    const borrowerSeat = lent.seat ?? (lent.side === "me" ? fromIdx : livingEnemySeatsOf(live, fromIdx)[0]);
    if (borrowerSeat == null) return;
    const bf = fieldOf(borrowerSeat);
    if (lent.kind === "escendant" && bf.esclings[lent.slotIdx]) bf.esclings[lent.slotIdx] = { ...bf.esclings[lent.slotIdx], cuirassBuffFrom: null };
    else if (lent.kind === "tome" && bf.tomes[lent.slotIdx]) bf.tomes[lent.slotIdx] = { ...bf.tomes[lent.slotIdx], cuirassBuffFrom: null };
    // whole-array writes go DENSE — a hole in an RTDB-shaped array is an
    // `undefined` the SDK refuses (see gameManager.setDeep, 2026-09-16)
    if (borrowerSeat !== fromIdx) paths[`players/${borrowerSeat}/field/${lent.kind === "escendant" ? "esclings" : "tomes"}`] = denseNull(lent.kind === "escendant" ? bf.esclings : bf.tomes);
    paths[`players/${fromIdx}/field/tomes/${i}`] = myTomesArr[i];
  });
  // A borrower on THIS seat whose source Cuirass is gone loses the +3.
  const sourceGone = (ref) => { const src = myTomesArr[ref?.tomeSlotIdx]; return !src?.id || baseId(src.id) !== "tome_chief_calidum"; };
  myRifts.forEach((s, i) => {
    if (s?.cuirassBuffFrom && sourceGone(s.cuirassBuffFrom)) myRifts[i] = { ...s, cuirassBuffFrom: null, currentLyf: cardOf(s.escendant)?.lyf ?? s.currentLyf };
  });
  myTomesArr.forEach((tm, i) => {
    if (tm && typeof tm === "object" && tm.cuirassBuffFrom && sourceGone(tm.cuirassBuffFrom)) {
      myTomesArr[i] = { ...tm, cuirassBuffFrom: null };
      paths[`players/${fromIdx}/field/tomes/${i}`] = myTomesArr[i];
    }
  });

  // R28 + the latches.
  if (seat.field?.esclings) paths[`players/${fromIdx}/field/esclings`] = denseNull(myRifts);

  // Khao Garden / Khao Arcadia — paid at the end of the Turn they fired.
  let maxKhao = seat.maxKhao || STARTING_KHAO;
  const tomes = seat.field?.tomes || {};
  for (const [i, tome] of Object.entries(tomes)) {
    if (!tome || typeof tome !== "object" || tome.faceDown) continue;
    const bump = END_TURN_MAX_KHAO_CARDS[baseId(tome.id)];
    if (!bump) continue;
    if (tome.lastUsedTurn !== turn || tome.endPaidTurn === turn) continue;
    maxKhao = Math.min(MAX_KHAO_ABS, maxKhao + bump);
    paths[`players/${fromIdx}/field/tomes/${i}/endPaidTurn`] = turn;
  }
  if (maxKhao !== (seat.maxKhao || STARTING_KHAO)) paths[`players/${fromIdx}/maxKhao`] = maxKhao;

  // R26 — the incoming seat's Fallen rise.
  const inc = live?.players?.[nextActive];
  const incRifts = inc?.field?.esclings;
  if (incRifts && nextActive !== fromIdx) {
    for (const [i, s] of Object.entries(incRifts)) {
      if (s?.fallen) { paths[`players/${nextActive}/field/esclings/${i}/fallen`] = false; paths[`players/${nextActive}/field/esclings/${i}/fallenTurn`] = null; }
    }
  }
  return paths;
}

export async function handOffHalfTurn(code, match, fromIdx, opts = {}) {
  const nextTurn = (match?.turn || 0) + 1;

  // ── WHO SITS DOWN NEXT ────────────────────────────────────────────
  // `opts.nextIdx` used to be preferred UNCONDITIONALLY, and that made
  // this function a rubber stamp for the one decision it exists to
  // make. Shatterfield's handleEndTurn computed its `nextActiveIdx`
  // from the React `match` STATE variable and then handed this function
  // `matchRef.current` — so the fresh snapshot was passed in and
  // immediately discarded in favour of a seat derived from a board one
  // render old. In a 3-4p FFA where a seat was knocked out since that
  // render, the written `activePlayer` was an ELIMINATED SEAT and the
  // table stalled with nobody able to move. (handleEndTurn now derives
  // its own value from the live board too — belt AND braces, because
  // this function is the one that writes the number.)
  //
  // ⚠ THE OVERRIDE IS NOT SIMPLY IGNORED, and the narrowness is
  // deliberate. NPC.js passes a HARDCODED `nextIdx: 0` and its comment
  // explains why: it is preserving the exact write npcHandOffTurn made
  // before this function existed, including in the window where seat 0
  // is already down but the match has not been marked finished yet.
  // Recomputing there would hand the chair back to the NPC — the only
  // living seat — and give it a second turn. So an override is only
  // overruled when there is somewhere ELSE for the chair to go: the
  // recomputed seat must be living AND must not be `fromIdx` itself.
  let nextActive = opts.nextIdx != null ? opts.nextIdx : getNextActiveSeat(match, fromIdx);
  if (opts.nextIdx != null && isEliminated(match, nextActive)) {
    const alt = getNextActiveSeat(match, fromIdx);
    if (alt !== nextActive && alt !== fromIdx && !isEliminated(match, alt)) nextActive = alt;
  }

  // ── 1 · THE LIVE BOARD ────────────────────────────────────────────
  // Read once. `match` is whatever the caller was holding when the turn
  // began — NPC.js in particular hands its brains' entry snapshot
  // straight through — and every Anima gained or lost DURING the turn
  // landed in Firebase after that snapshot was taken. Rotating a stale
  // ledger would file the turn's own inflow as if it had never
  // happened, so the rotation is computed off THIS, never off `match`.
  //
  // buildEndOfHalfTurnUpdate touches exactly three keys and
  // `totalAnimaLost` is deliberately NOT one of them: that one is
  // cumulative for the whole Solrend (Bloody Hell reads it) and zeroing
  // it would silently disarm a Prophecy.
  const live = await getMatch(code);
  const outgoing = live?.players?.[fromIdx] || match?.players?.[fromIdx] || null;
  const ledger = outgoing ? buildEndOfHalfTurnUpdate(outgoing) : null;

  // ── THE COST OF A HAND-OFF, AND WHY IT IS THE SHAPE IT IS ─────────
  // Measured before this pass: 2 reads and 2 writes, every half-turn,
  // in both drivers. Over a 150-half-turn duel that is 300 reads and
  // 300 writes of real Firebase traffic bought by a refactor.
  //
  // ⚠ RE-MEASURED 2026-08-25 AGAINST THE FIREBASE SHIM, one hand-off at
  // a time, so the number in this banner is an observation rather than
  // an intention:
  //     ordinary hand-off ............ 1 read,  1 write
  //     hand-off that rings R93 ...... 1 read,  2 writes
  //     over a 150-half-turn duel .... 150 reads / 150 writes
  // Halved on both axes. (The clock path gained a THIRD write in the
  // rare case where R98 finds a co-winner and has to stamp its
  // Prophecy spent — a duel that ends, at most once, so it does not
  // enter the per-half-turn figure.) Two things were paid for before
  // and neither was needed:
  //
  //   • THE SECOND READ. Step 4 re-read the match to get "the newest
  //     board" — a path this function itself had written two lines
  //     earlier, from values it already held. It is composed locally
  //     now, from `live` plus the exact patch that was just written.
  //     Nothing is guessed: the composition is the write.
  //   • THE LEDGER WRITE. The rotation went out as its own
  //     updatePlayer, immediately before an updateMatch to the parent
  //     path. Firebase's multi-path update takes slash keys, so both
  //     land in ONE atomic write instead of two — the same trick
  //     bossPhases.escalationUpdates uses, and for the same reason: a
  //     hand-off that can be observed half-applied is a hand-off that
  //     can be observed wrong.
  const ledgerPaths = {};
  if (ledger) for (const [k, v] of Object.entries(ledger)) ledgerPaths[`players/${fromIdx}/${k}`] = v;
  // ⭐ PHASE 5, ONCE — see the banner above handOffHalfTurn.
  Object.assign(ledgerPaths, endPhasePaths(live || match, fromIdx, nextActive));

  // ── R92b · KHAO REPLENISHES AT THE START OF A TURN ────────────────
  // caP'n, 2026-09-13, asked which end of the Turn the pool refills on:
  //
  //   *"Yes, at the beginning of every single turn, regardless if you
  //   used any or not, replenish your current Max Khao to its full
  //   amount."*
  //
  // ⚠ THE BOARD REFILLED THE OUTGOING SEAT, AND THAT IS A DIFFERENT
  // GAME. Shatterfield's two hand-offs and all three NPC brains wrote
  // `khao: maxKhao` onto the seat whose Turn was ENDING, so every seat
  // sat through its adversary's half-turn holding a FULL pool and could
  // answer, counter and fire soul cards out of it for free. Under the
  // rule, a seat crosses into its adversary's half-turn holding
  // whatever it did not spend, and the refill is waiting for it when
  // its own Turn opens.
  //
  // ⚠ AND IT LIVES HERE, IN THE ONE FUNCTION EVERY TRANSITION SHARES,
  // for the reason the ledger rotation moved here: the same value
  // written in five places is five places for it to drift, and it had
  // already drifted once (the timer's hand-off kept the Cuirass buff;
  // the button's did not). It rides `ledgerPaths` into handOffChair's
  // transaction, so the chair and the pool move in ONE atomic write and
  // no client can observe a seat sitting down with a stale pool.
  //
  // ⚠ `maxKhao` IS READ OFF `live`, NOT `match` — a card that raised the
  // incoming seat's ceiling during the turn that just ended (Khao
  // Garden, Beb Buddy, the Warden's bounty) landed in Firebase after
  // the caller's snapshot was taken, and refilling to a stale ceiling
  // would quietly delete the card's effect. `khaoSpentThisCycle` rides
  // the same read for the same reason, and a sharper one: the payment
  // that most often breaks a seat's climb is a COUNTER it fired on the
  // half-turn that has just ended, which is a write `match` cannot
  // possibly contain.
  //
  // ── R132 · AND THE CLIMB LANDS IN THE SAME BREATH ─────────────────
  // caP'n, 2026-09-15, asked WHEN the +1 to Max Khao arrives:
  //
  //   *"after 1 full turn of not using Khao, so if you didn't use any
  //   turn 1, then the beginning of turn 2 you get +1 to your Max Khao
  //   hopefully that also clarifies other rulings as far as how the 1
  //   full turn etc works"*
  //
  // R12 already said WHAT breaks the climb — one full Turn must pass
  // with NO Khao spent at all, the seat's own half-turn PLUS its
  // adversary's, and every payment breaks it including a counter fired
  // on the adversary's turn. This ruling fixes the MOMENT: the
  // BEGINNING of the Turn. That moment is this seam.
  //
  // ⚠ IT WAS FOUR SITES AND THEY DID NOT AGREE. The human seat granted
  // at the START of its turn (a Shatterfield effect) and all three NPC
  // runners granted at END TURN, in the same payload that cleared the
  // spend flag. `match.turn` counts HALF-turns from 0, so in 1v1 seat 0
  // takes half-turns 0, 2, 4 and seat 1 takes 1, 3, 5. The human
  // evaluated as it sat down INTO half-turn 2 and PLAYED half-turn 2 on
  // a Max Khao of 4; a brain evaluated as it stood UP from half-turn 3
  // and first PLAYED on 4 at half-turn 5. The brain was a full Turn
  // behind the player on Max Khao for the entire duel. Nobody chose
  // that — it was drift between four copies of one rule, which is the
  // same disease the refill above was moved here to cure. There is now
  // ONE grant site in the engine and this is it.
  //
  // ⚠ THE REFILL GOES TO THE CLIMBED CEILING. A seat that climbs to 4
  // sits down holding 4, not 3 — otherwise the +1 it earned is
  // invisible for the whole Turn it was earned for. (The human path
  // already wrote `{ maxKhao: newMax, khao: newMax }` together; the
  // Debugger's simulator does the same.)
  //
  // ⚠ THE NATURAL CEILING NEVER WRITES DOWNWARD: `current <
  // MAX_KHAO_NAT ? current + 1 : current`, and never
  // `Math.min(MAX_KHAO_NAT, current + 1)`. R13 stops the NATURAL climb
  // at 10, but a CARD may push the ceiling to MAX_KHAO_ABS, and the
  // clamp form wrote a seat sitting on 12 after a Khao Arcadia back
  // down to 10 — the card's whole effect deleted by a bookkeeping line.
  // Both NPC.js and the Debugger's simulator record fixing exactly that
  // bug in their own copies; this one is written so it cannot recur.
  //
  // ⚠ maxKhao IS WRITTEN ONLY WHEN IT ACTUALLY MOVED. An unconditional
  // write would put `live`'s ceiling back over anything that raised it
  // between that read and this transaction — the one hazard the banner
  // above names — and buy nothing on the Turns where nothing climbed.
  //
  // ⚠ THE SPEND FLAG IS CLEARED HERE AND NOWHERE ELSE FOR THIS SEAT.
  // The window R12 measures runs from one sit-down to the next. The
  // brains used to clear it as they STOOD UP, which pointed the window
  // at the wrong half of the Turn; a second clear anywhere would shorten
  // it and let the counter fired on the adversary's turn — the payment
  // caP'n named explicitly — escape the rule written to catch it.
  //
  // ⚠ THE GATE IS SEAT-COUNT AWARE, AND AT 1v1 IT IS EXACTLY
  // FIRST_KHAO_TURN. R91 says the climb starts on Turn 2 and
  // FIRST_KHAO_TURN is that 2 — but the number it is compared against
  // counts HALF-turns, and "the opening Turn of the duel" at a 3-4 seat
  // table is the opening LAP (R127; R08's opening Cycle makes the same
  // generalisation, and `halfTurnsPerFullTurn` IS `openingCycleLength`,
  // so the R12 unit and the R91 window cannot drift apart). A bare
  // `>= 2` handed the seats sitting down into half-turns 2 and 3 — their
  // FIRST turns at a four-seat table — a climb they had not played a
  // single Turn to earn. `Math.max` is what makes the floor able only
  // to rise: at two seats the lap is 2 and this is byte-identical to
  // `nextTurn >= FIRST_KHAO_TURN`.
  //
  // Asked on the INCREMENTED turn — the turn the seat is sitting down
  // INTO — the same reading, for the same reason, as R93 below.
  //
  // ⚠ ONE GRANT PER LAP FALLS OUT OF THE KEYING, not out of arithmetic.
  // The climb is computed for the seat SITTING DOWN, and each seat is
  // the incoming seat exactly once per lap of the table however many
  // chairs there are. There is no modulus here and there must not be
  // one: `halfTurnsPerFullTurn` is a FLOOR on the opening, not a period.
  const incomingSeat = (live || match)?.players?.[nextActive];
  if (incomingSeat) {
    const currentMax = incomingSeat.maxKhao || STARTING_KHAO;
    // R91/R132 — the floor lives once, in rules.khaoClimbFloor (2026-09-15).
    const climbFloor = khaoClimbFloor(seatCountOf(live || match));
    const climbed = (nextTurn >= climbFloor
                     && !incomingSeat.khaoSpentThisCycle
                     && currentMax < MAX_KHAO_NAT)
      ? currentMax + 1
      : currentMax;
    if (climbed !== currentMax) ledgerPaths[`players/${nextActive}/maxKhao`] = climbed;
    ledgerPaths[`players/${nextActive}/khao`] = climbed;
    ledgerPaths[`players/${nextActive}/khaoSpentThisCycle`] = false;
  }

  // ── 2 · R93 · THE SECOND HAND ─────────────────────────────────────
  // A Solrend is bounded in HALF-TURNS as well as in R87's twenty
  // minutes. Ask on the INCREMENTED turn, and WRITE that turn before
  // ending: a duel called at 150 half-turns archived at 149 is a stored
  // state that contradicts the rule that ended it, and every replay and
  // end screen downstream reads the wrong number.
  // ⚠ THE VERDICT IS ASKED OF `live`, NOT OF `match`, AND THAT IS THE
  // WHOLE POINT OF READING `live` ABOVE. `match` is the snapshot the
  // CALLER was holding when the half-turn began — NPC.js takes it at
  // the top of runNPCTurn and threads it through the entire turn, and
  // Shatterfield's callers pass `matchRef.current`, which is a render
  // behind at worst. Every Anima dimmed or gained DURING the turn
  // landed in Firebase AFTER that snapshot was taken, and R93's verdict
  // is `timeoutWinner` — i.e. WHO HAS MORE ANIMA. Asking the entry
  // board therefore hands the duel to whoever was ahead when the turn
  // STARTED: an entry snapshot of 30 vs 12, a live board of 5 vs 12,
  // and the seat sitting on 5 wins.
  //
  // ⚠ `turn` STILL COMES FROM THE CALLER, deliberately. `nextTurn` is
  // `match.turn + 1`, and NPC.js's turn-0 setup path hands this
  // function `{ ...match, turn: 0 }` on purpose so its hand-off is
  // half-turn 1 and the bell cannot ring. That override survives here
  // untouched — only the BOARD is taken from the live read.
  //
  // Measured before this fix: 5 seeds x 40 games, 19 clock calls, ZERO
  // disagreements between the two boards — a duel that reaches 150
  // half-turns is a locked one where nothing moves, so the two answers
  // agree in practice today. It is fixed anyway: R98's co-winner check
  // eight lines below has always asked `live`, so the two halves of one
  // ending were reading two different boards, and that divergence is
  // precisely what this module exists to abolish.
  const verdict = callOnClock({ ...(live || match), turn: nextTurn }, opts.clockOpts || {});
  if (verdict) {
    // The ledger rides along. It used to be written before this branch
    // was even reached, so dropping it here would have been a silent
    // behaviour change on the clock path — the transition referee's X06
    // reconstruction detects the rotation from `turn` advancing and
    // would have started inventing fares.
    await updateMatch(code, { ...ledgerPaths, turn: nextTurn, calledOnClock: verdict.rule });
    const clockWinners = await endOnClock(code, { ...(live || match), turn: nextTurn }, verdict);
    return {
      calledOnClock: true, verdict, ended: true,
      winners: clockWinners,
      prophecyEnded: false, prophecy: null,
      nextTurn, nextActive,
    };
  }

  // ── 3 · THE HAND-OFF ITSELF ───────────────────────────────────────
  // GUARDED. See handOffChair's banner: several clients can legitimately
  // reach a grace-expiry hand-off in the same second once a table seats
  // more than two, and an unguarded update() lets the second one advance
  // a chair that has already moved. `applied: false` is a normal outcome
  // — somebody else did the work — and the caller stops rather than
  // narrating a turn it did not actually cause.
  const handed = await handOffChair(code, fromIdx, nextActive, nextTurn, ledgerPaths);
  if (!handed.applied) {
    return {
      calledOnClock: false, verdict: null, ended: false, alreadyHandedOff: true,
      prophecyEnded: false, prophecy: null, nextTurn, nextActive,
    };
  }

  // ── 4 · PROPHECIES, ON THE NEWEST BOARD ───────────────────────────
  // COMPOSED, not re-read. The board a Prophecy must be judged against
  // is `live` plus exactly what step 3 wrote, and step 3 wrote three
  // things this function chose itself: the rotated ledger on `fromIdx`,
  // the new `activePlayer`, and the new `turn`. Re-reading to discover
  // values you just supplied is a round-trip that can only ever agree
  // with you — unless another writer landed in the gap, in which case
  // the re-read version was racing anyway and would simply have lost
  // differently. So it is built here, and the stale-closure hazard the
  // old comment was written about is answered by `live` being read
  // AFTER the caller's own board writes, not by reading it twice.
  let prophecy = null;
  if (!opts.skipProphecies && live) {
    const fresh = {
      ...live,
      turn: nextTurn,
      activePlayer: nextActive,
      players: outgoing && ledger
        ? { ...(live.players || {}), [fromIdx]: { ...outgoing, ...ledger } }
        : live.players,
    };
    if (fresh.status !== "finished") {
      try {
        prophecy = await checkProphecies(code, fresh);
      } catch (e) {
        console.error("[Prophecy] check error:", e && e.message);
      }
    }
  }

  // `prophecyEnded` means "a Prophecy came true here" (which in a 3-4p
  // FFA may only have knocked one seat out); `ended` means the SOLREND
  // is over. In 1v1 they are the same fact, which is exactly why they
  // are two fields rather than one.
  return {
    calledOnClock: false, verdict: null,
    ended: !!(prophecy && prophecy.ended),
    prophecyEnded: !!prophecy, prophecy,
    nextTurn, nextActive,
  };
}

// ── THE RESEAT INJECTION ────────────────────────────────────────────
// matchOutcome.js has to move the chair when a mid-turn elimination
// knocks out whoever was holding it, and moving the chair is THIS
// file's whole job. It cannot import this module: prophecyEngine
// imports matchOutcome and this file imports prophecyEngine, so the
// arrow only goes one way and a straight import would close the loop
// AND give handOffHalfTurn a path back into itself through
// checkProphecies.
//
// So the dependency is INJECTED, the same way ruleGate takes its
// effective-Lyf resolver from Shatterfield. This module already sits
// above matchOutcome in the graph, so registering on load costs
// nothing and adds no cycle. matchOutcome keeps its own inline reseat
// as a fallback for any consumer that somehow never loads this file.
//
// It is always called with `skipProphecies: true`, and that is the
// recursion guard: without it, a Prophecy knockout that vacates the
// chair would ask for Prophecies again from inside its own resolution.
setHandOffHalfTurn(handOffHalfTurn);

const turnEngine = { handOffHalfTurn, endOnClock };
export default turnEngine;
